// WebsiteAdV2.js — wrapper around a denormalized `websiteAdsV2` doc (#487).
//
// Adapter: exposes the SAME accessor surface the listing/detail views already
// consume from the v1 WebsiteAd (getTitle, getPrice, features, images +
// imageBlurhashes, publicStatus tags, getProjectStatus, …) so the views can
// render either collection with minimal markup changes (the two still coexist:
// see "Where v1 is still load-bearing" in docs/websiteAdsV2.md). All the v1→v2
// gap resolutions live here, in one place.

import BaseAddressInfo from './BaseAddressInfo';
import WebsiteAdChild from './WebsiteAdChild';
import { housetype } from '../app_const';
import { MARKET_NEGOTIATION, MARKET_SOLD, normalizeMarket, activePriceDrop } from '../utils/market';
import {
    galleryToLegacy,
    primaryStyleCardEntries,
    cardPhotoRenderPairs,
    entryDisplayUrl,
    entryImageSet,
    entrySrcSet,
    agencyLogoUrl,
} from '../services/adImageSource';

// Gap G1: v2 has no publicStatus tags; 'Novità!' is derived from how recent
// the publication is.
export const NOVITA_WINDOW_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

// Add-on "Progetto AI" (newarc-site #707): id written by websiteSync in
// `addOns[]` when the ad's redistribution add-on is ACTIVE. On such ads
// `planimetrie.project` is the designers' redistributed floor plan (a new
// layout, not the current one furnished) and the Immagina renders show that
// new layout.
export const REDISTRIBUTION_ADDON_ID = 'redistribuzione';

// Add-on "Render Smart" (#760): the agency uploads its own project plan (the
// new layout, already decided) and the Newarc designers deliver up to 5 renders
// on it. On such ads `planimetrie.project` is the agency's project plan
// (furnished by the designers when they delivered one) and the only renders
// are the designers': no AI content of the ad is project content.
export const RENDER_SMART_ADDON_ID = 'renderSmart';

// Add-ons whose project plan is a NEW distribution of the spaces, in the sync's
// precedence order (contract `catalogo.progetto`: with both active,
// Ridistribuzione wins). They share the "Nuova distribuzione" plan label and
// card badge, and hide the redistribution CTA. Render Pro is not here: its
// plan is not a new layout.
export const NEW_DISTRIBUTION_ADDON_IDS = [REDISTRIBUTION_ADDON_ID, RENDER_SMART_ADDON_ID];

// Blocco `project` — modalità Progetto (newarc-site #719, contratto dati in
// docs/progetto-contratto-dati.md; scritto da websiteSync, cloud-functions
// #338). Presente solo sugli annunci con un potenziamento di progetto.
export const PROJECT_SCHEMA_VERSION = 1;

function isImageEntry(e) {
    return !!e && typeof e === 'object' && typeof e.path === 'string';
}

// Lettura difensiva del blocco: null quando assente (annuncio normale). I campi
// facoltativi del contratto (deliveredAt, works, facts, designer, disclaimer,
// label del piano) diventano null/[] quando mancano, così il sito usa i suoi
// ripieghi; un piano senza uno dei due lati si scarta.
export function normalizeProjectBlock(raw) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
    const text = (v) => (typeof v === 'string' && v.trim() ? v : null);
    return {
        schemaVersion: raw.schemaVersion ?? PROJECT_SCHEMA_VERSION,
        addOn: text(raw.addOn),
        status: raw.status === 'delivered' ? 'delivered' : 'pending',
        deliveredAt: typeof raw.deliveredAt === 'number' ? raw.deliveredAt : null,
        renders: Array.isArray(raw.renders) ? raw.renders.filter(isImageEntry) : [],
        plans: Array.isArray(raw.plans)
            ? raw.plans
                .filter((p) => p && isImageEntry(p.current) && isImageEntry(p.project))
                .map((p) => {
                    const pair = { label: text(p.label), current: p.current, project: p.project };
                    // Gli ambienti ci sono solo quando la sync li pubblica
                    // (`plans[].tags`, newarc-cloud-functions #629).
                    const tags = planTags(p);
                    if (tags.length) pair.tags = tags;
                    return pair;
                })
            : [],
        works: Array.isArray(raw.works)
            ? raw.works
                .filter((w) => w && text(w.text))
                .map((w) => ({ text: w.text, kind: text(w.kind) }))
            : [],
        facts: raw.facts && typeof raw.facts === 'object' && !Array.isArray(raw.facts) ? raw.facts : null,
        designer: raw.designer && text(raw.designer.name)
            ? { name: raw.designer.name, title: text(raw.designer.title) }
            : null,
        disclaimer: text(raw.disclaimer),
    };
}

// Ambienti della pianta di progetto (projectTags della piattaforma, #3312):
// etichetta e posizione (0..1 sull'immagine della pianta di progetto). Si
// leggono da `project.plans[].project.tags` (o `tags` sulla coppia); scarta
// le voci senza etichetta o fuori dall'immagine.
function planTags(pair) {
    const raw = (pair.project && Array.isArray(pair.project.tags) && pair.project.tags)
        || (Array.isArray(pair.tags) && pair.tags) || [];
    const unit = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
    return raw
        .filter((t) => t && typeof t.label === 'string' && t.label.trim() && unit(t.xPercent) && unit(t.yPercent))
        .map((t) => ({ label: t.label.trim(), x: t.xPercent, y: t.yPercent }));
}

/**
 * Blocco video di websiteAdsV2 (`video` / `videoVertical`, #700 #728):
 * `{ path, url, posterUrl, hdUrl, sdUrl }` oppure null quando non c'è un url
 * da riprodurre. `url` è sempre l'originale; `hdUrl`/`sdUrl` sono le versioni
 * compresse dal server (lato corto 1080/720), presenti quando sono pronte.
 * Le facoltative mancanti valgono ''.
 */
export function normalizeVideoBlock(raw) {
    if (!raw || typeof raw !== 'object' || typeof raw.url !== 'string' || !raw.url) return null;
    const text = (v) => (typeof v === 'string' ? v : '');
    return {
        path: text(raw.path),
        url: raw.url,
        posterUrl: text(raw.posterUrl),
        hdUrl: text(raw.hdUrl),
        sdUrl: text(raw.sdUrl),
    };
}

/**
 * Quale video apre la pillola "Video" e da quale file (#728): da telefono il
 * verticale se c'è, altrimenti l'orizzontale; da computer l'orizzontale se
 * c'è, altrimenti il verticale. Il file: da telefono la versione sd (poi hd),
 * da computer la hd; se le versioni compresse non sono ancora pronte,
 * l'originale `url`. `{ url, posterUrl, vertical }` oppure null senza video;
 * `vertical` dice quale dei due è stato scelto (layout del player).
 */
export function pickAdVideo(horizontal, vertical, isPhone) {
    const order = isPhone
        ? [[vertical, true], [horizontal, false]]
        : [[horizontal, false], [vertical, true]];
    for (const [v, isVertical] of order) {
        if (!v || !v.url) continue;
        const sources = isPhone ? [v.sdUrl, v.hdUrl, v.url] : [v.hdUrl, v.url];
        const url = sources.find((u) => typeof u === 'string' && u);
        return { url, posterUrl: v.posterUrl || '', vertical: isVertical };
    }
    return null;
}

function normalizeGalleries(galleries) {
    return {
        current: Array.isArray(galleries?.current) ? galleries.current : [],
        project: Array.isArray(galleries?.project) ? galleries.project : [],
    };
}

export default class WebsiteAdV2 {
    // `now` is injectable for tests; defaults to the real clock.
    constructor(data = {}, { now = Date.now() } = {}) {
        // v2 identity / provenance
        this.schemaVersion = data.schemaVersion || 2;
        this.sourceType = data.sourceType || '';
        this.sourceId = data.sourceId || '';
        this.code = data.code || '';
        this.isArchived = data.isArchived ?? false;
        this.isActive = data.isActive ?? true;
        this.publicationDate = data.publicationDate || 0;
        // Last publication (platform gh-2972): what the listings order by.
        // 0 on ads that predate the field — see publicationSortDate().
        this.lastPublicationDate = data.lastPublicationDate || 0;
        this.syncedAt = data.syncedAt || 0;
        // Multi-taglio (newarc-cloud-functions#216): every taglio of a
        // multi-unit immaginaCasa ad gets its OWN websiteAdsV2 doc, carrying
        // here the id of its parent doc. Set ⇒ this is a child: reachable by
        // direct link (/annunci/<id>, the "Tipologie" block's "Entra" button)
        // but excluded from the listings, which show the parent only. Parents
        // — and every doc predating #216 — have no such field ⇒ null.
        this.parentAdId = data.parentAdId || null;
        // Anteprima privata (#915): sha256 del token del link condivisibile
        // di un annuncio non ancora pubblicato, scritto da websiteSync; null
        // senza anteprima. Usato SOLO da un link diretto con `?anteprima=`,
        // mai dalle liste (che filtrano isActive server-side).
        this.previewTokenHash =
            typeof data.previewTokenHash === 'string' && data.previewTokenHash.length === 64
                ? data.previewTokenHash.toLowerCase()
                : null;

        // v2 property fields
        this.title = data.title || '';
        this.propertyType = data.propertyType || '';
        this.isNewConstruction = data.isNewConstruction ?? false;
        // Annuncio del modello modulare (piattaforma #2253): usato per
        // distinguere i percorsi "in costruzione" senza foto dello stato di
        // fatto (solo render). Assente sui documenti pre-esistenti ⇒ false.
        this.isUnifiedAd = data.isUnifiedAd ?? false;
        this.cuts = data.cuts || 0;
        this.price = data.price ?? null;
        this.condominiumFees = data.condominiumFees ?? null;
        this.commercialSurface = data.commercialSurface || 0;
        this.rooms = data.rooms || 0;
        this.bathrooms = data.bathrooms || 0;
        this.floor = data.floor || '';
        this.energyClass = data.energyClass || '';
        this.propertyCondition = data.propertyCondition || '';
        this.constructionYear = data.constructionYear ?? null;
        this.heating = data.heating || '';
        this.exposure = Array.isArray(data.exposure) ? data.exposure : [];
        // v2 `features` is the list of characteristics ("Balcone", …); the
        // legacy `features` name is taken by the v1 icon map built below.
        this.characteristics = Array.isArray(data.features) ? data.features : [];
        this.description = data.description || '';
        this.renovationEstimate = data.renovationEstimate || null;
        this.agency = data.agency || null;
        // Ids of the ad's ACTIVE add-ons, as synced by websiteSync (#707).
        // Absent on older docs ⇒ [].
        this.addOns = Array.isArray(data.addOns)
            ? data.addOns.filter((a) => typeof a === 'string')
            : [];
        // "Progetto AI": the plans/renders show a NEW distribution of the
        // spaces proposed by the designers. See hasNewDistribution below for
        // the labels, notes, badge and CTA, shared with Render Smart.
        this.hasRedistribution = this.addOns.includes(REDISTRIBUTION_ADDON_ID);
        // "Render Smart" (#760): the agency's project plan, designer renders.
        this.hasRenderSmart = this.addOns.includes(RENDER_SMART_ADDON_ID);
        // The active add-on that makes the project plan a NEW distribution
        // (Progetto AI or Render Smart), or null. Drives the shared
        // "Nuova distribuzione" wording; the add-on picks the attribution copy.
        this.newDistributionAddOn =
            NEW_DISTRIBUTION_ADDON_IDS.find((id) => this.addOns.includes(id)) || null;
        this.hasNewDistribution = this.newDistributionAddOn !== null;
        // Progetto AI su un cambio di destinazione d'uso (#964): websiteSync
        // scrive `changeOfUse: true` solo con Progetto AI attivo. La pillola
        // di Progetto AI diventa "Cambio d'uso - Progetto AI".
        this.hasChangeOfUse = this.hasRedistribution && data.changeOfUse === true;
        // Modalità Progetto (#719): il blocco `project` letto dal contratto; la
        // modalità esiste solo a progetto consegnato con almeno un render.
        this.project = normalizeProjectBlock(data.project);
        this.hasProjectMode = !!this.project
            && this.project.status === 'delivered'
            && this.project.renders.length > 0;

        // Commercial status (#782, contract in src/utils/market.js). A sold ad
        // stays published for 30 days: "Venduto" badge in the lists for 72 h,
        // then delisted (out of every list, page still reachable by link).
        // Absent on every doc synced before the field ⇒ available, no drop.
        const market = normalizeMarket(data.market);
        this.marketStatus = market.status;
        this.isSold = market.status === MARKET_SOLD;
        this.isUnderNegotiation = market.status === MARKET_NEGOTIATION;
        this.isDelisted = this.isSold && market.delisted;
        this.soldAt = market.soldAt;
        // The latest qualifying drop while it is news (30 days), else null.
        this.priceDrop = activePriceDrop(market.priceDrop, now);
        // What cards and detail page show ("Prezzo ribassato", the struck-
        // through price): never on a sold ad, which is no longer an offer.
        this.hasPriceDrop = this.priceDrop !== null && !this.isSold;

        // Raw prefetched galleries (detail page: before/after + planimetrie).
        this.imagesV2 = normalizeGalleries(data.images);
        this.planimetrie = normalizeGalleries(data.planimetrie);
        // Video dell'annuncio (Video Render, newarc-site #700): scritto da
        // websiteSync come { path, url, posterUrl? } oppure null. `posterUrl` è
        // la copertina (#728), facoltativa: '' quando manca. Video Render
        // consegna sempre due video (#728): `video` orizzontale e
        // `videoVertical` verticale, stessa forma; ognuno può mancare.
        this.video = normalizeVideoBlock(data.video);
        this.videoVertical = normalizeVideoBlock(data.videoVertical);
        // Copertina del video orizzontale (#728): '' senza video o senza copertina.
        this.videoPoster = this.video ? this.video.posterUrl : '';

        // ── legacy (v1 WebsiteAd) accessor surface ──
        this.newarcHomeId = this.sourceId;
        this.type = this.propertyType;
        this.floorArea = this.commercialSurface;
        this.roomCount = this.rooms;
        this.baths = this.bathrooms;
        this.floors = this.floor;
        this.projectType = '';
        this.agencyId = this.agency?.agencyId || '';
        this.agencyLogoImage = this.agency?.logo || '';
        this.addressInfo = new BaseAddressInfo(data.addressInfo || {});
        // Multi-unit tagli (immaginaCasa, platform#2088 fase 2): websiteSync
        // mirrors the parent's units here in the v1 WebsiteAdChild shape.
        // Only ACTIVE units are public — the parent doc's isActive is derived
        // (any unit active), so a listed parent always has >= 1 child here.
        // Wrapped as WebsiteAdChild so every card surface (ListingCard calls
        // getTitle()/getFormattedPrice()) and the detail mapping consume the
        // same shape the v1 path already produces.
        this.children = Array.isArray(data.children)
            ? data.children
                .filter((c) => c && c.isActive === true)
                .map((c) => new WebsiteAdChild(c))
            : [];
        this.displayStatus = 1;
        this.zone = data.zone || '';

        // Card images: the PHOTOGRAPHS of the current state, each paired with
        // the furnished render of the same framing, which the card reveals
        // under a lens on hover.
        //
        // Showing the renders themselves (what this did until now) made every
        // ad in the list look the same: the card isolates ONE furnishing style,
        // so every flat was staged `rome` (the first of STYLE_ORDER), one room
        // per type, and the eye could not tell one property from another. The
        // photographs differ as much as the flats do; the render stays one
        // gesture away. cardPhotoRenderPairs pairs them per SLOT (tag +
        // occurrence), so the lens always reveals the same room, in the ad's
        // own primary style, never another slot's or another style's render.
        // That primary style is no longer always `rome`: it is picked per ad
        // among the ad's styles, so the lenses differ across the list too.
        //
        // No pair — no photographs ("in costruzione"), no furnishing (a
        // renovation-only ad), a migrated newarcHome ad — falls back to the
        // behaviour that predates the lens, and the card shows no lens at all.
        // #645: the project gallery also carries the pipeline's intermediate
        // steps (nakedInteriors, the neutral renovation render) and, one day,
        // types this build has never heard of. primaryStyleCardEntries applies
        // the shared visibility policy (the furnished renders, else the STYLED
        // renovation renders alone, else the neutral one alone as a legacy
        // fallback, else nothing), then isolates ONE AI furnishing style so the
        // card's arrows step room by room through that style instead of mixing
        // every style's renders into one slider.
        // The card's style varies ad by ad (pickPrimaryStyle), seeded by the
        // ad's id so each ad keeps the same one across reloads.
        const styleSeed = this.sourceId || this.code;
        const pairs = cardPhotoRenderPairs(this.imagesV2.project, this.imagesV2.current, { seed: styleSeed });
        if (pairs.length > 0) {
            const card = galleryToLegacy(pairs.map((p) => p.photo));
            this.images = card.images;
            this.imageBlurhashes = card.blurhashes;
            this.imageSrcsets = card.srcsets; // responsive srcset per card image (<img>)
            this.imageImageSets = card.imagesets; // image-set() per card image (background)
            // Index-aligned with `images`: the render the lens reveals over
            // card image i.
            this.imageRenders = pairs.map((p) => entryDisplayUrl(p.render));
            this.imageRenderImageSets = pairs.map((p) => entryImageSet(p.render));
            // Responsive srcset of each render, for an <img> (the home hero
            // showcase crossfades photo → render at full size).
            this.imageRenderSrcsets = pairs.map((p) => entrySrcSet(p.render));
        } else {
            const project = galleryToLegacy(primaryStyleCardEntries(this.imagesV2.project, { seed: styleSeed }));
            const current = galleryToLegacy(this.imagesV2.current);
            const card = project.images.length > 0 ? project : current;
            this.images = card.images;
            this.imageBlurhashes = card.blurhashes;
            this.imageSrcsets = card.srcsets; // responsive srcset per card image (<img>)
            this.imageImageSets = card.imagesets; // image-set() per card image (background)
            this.imageRenders = [];
            this.imageRenderImageSets = [];
            this.imageRenderSrcsets = [];
        }

        // Planimetria cover (platform coverPlanimetriaIndex): when the ad
        // designates a planimetria as its card cover, resolve that entry by
        // `index` from the already-processed planimetrie gallery (prefer the
        // generated plan, fall back to the uploaded source). The card renders
        // it as a single static image, not a slider.
        this.coverPlanimetriaIndex = Number.isInteger(data.coverPlanimetriaIndex)
            ? data.coverPlanimetriaIndex
            : null;
        this.coverIsPlanimetria = false;
        this.coverPlanimetria = null;
        if (this.coverPlanimetriaIndex !== null) {
            // Resolve the entry at this index only if it maps to a displayable
            // image; prefer the generated plan (project), fall back to the
            // uploaded source (current). A present-but-unprocessed project
            // entry (empty url) must NOT suppress a valid current fallback.
            const resolveFrom = (arr) => {
                const e = arr.find(
                    (x) => x && x.index === this.coverPlanimetriaIndex
                );
                if (!e) return null;
                const mapped = galleryToLegacy([e]);
                return mapped.images.length > 0 ? mapped : null;
            };
            const mapped =
                resolveFrom(this.planimetrie.project) ||
                resolveFrom(this.planimetrie.current);
            if (mapped) {
                this.coverIsPlanimetria = true;
                this.coverPlanimetria = {
                    image: mapped.images[0],
                    blurhash: mapped.blurhashes[0] || '',
                    srcset: mapped.srcsets[0] || '',
                    imageset: mapped.imagesets[0] || '',
                };
            }
        }
        // #942: an ad "in costruzione" of the unified model can be published
        // with the project plan only (no photo, no render): its card would be
        // empty. It shows the first plan instead, as a planimetria cover — the
        // uploaded one (`current`), else the project one. An ad with any card
        // image, or a designated cover, is untouched.
        if (!this.coverIsPlanimetria && this.images.length === 0
            && this.isUnifiedAd === true && this.isNewConstruction === true) {
            const mapped = [this.planimetrie.current, this.planimetrie.project]
                .map((arr) => galleryToLegacy(arr))
                .find((m) => m.images.length > 0);
            if (mapped) {
                this.coverIsPlanimetria = true;
                this.coverPlanimetria = {
                    image: mapped.images[0],
                    blurhash: mapped.blurhashes[0] || '',
                    srcset: mapped.srcsets[0] || '',
                    imageset: mapped.imagesets[0] || '',
                };
            }
        }

        // Agency logo fallback: the immaginaCasa sync doesn't populate
        // agency.logo, so derive it from the agencyId. The storage bucket comes
        // from one of the ad's own prefetched image URLs.
        if (!this.agencyLogoImage && this.agencyId) {
            const sample = [
                ...this.imagesV2.project, ...this.imagesV2.current,
                ...this.planimetrie.project, ...this.planimetrie.current,
            ].map((e) => e && e.url).find(Boolean);
            this.agencyLogoImage = agencyLogoUrl(this.agencyId, sample);
        }

        // Gap G1: only 'Novità!' is derivable client-side. 'Progetto Newarc' /
        // 'Partner verificato' are NOT claimed — sourceType alone would badge
        // partner ads too; a server-side badges[] field is the clean fix.
        const isRecent = now - this.publicationDate <= NOVITA_WINDOW_DAYS * DAY_MS;
        this.publicStatus = isRecent ? ['Novità!'] : [];
        this.hasProjectNewarcTag = false;
        this.hasPartnerVerificationTag = false;

        this.features = {
            'square-meters': this.floorArea + ' mq',
            'rooms': this.roomCount.toString() + ' locali',
            'bathrooms': this.baths + ' bagni'
        };
    }

    getDisplayType() {
        // Multi-unit immaginaCasa ads carry their tagli in children[]
        // (platform#2088 fase 2 — closes gap G2 for this source). Migrated
        // newarcHome multi-units still win from the v1 path in the listing,
        // so 'multiple' here only surfaces for immaginaCasa parents.
        return this.children.length > 0 ? 'multiple' : 'single';
    }

    houseTypes() {
        const types = [];
        for (const child of this.children) {
            if (child.type && !types.includes(child.type)) types.push(child.type);
        }
        return types;
    }

    maxPrice() {
        let max = 0;
        for (const child of this.children) {
            const p = Number(child.price) || 0;
            if (p > max) max = p;
        }
        return max;
    }

    // Gap G3: v2 has no v1 `status` enum; derive it for the card status pill
    // and the type filter (projectStatus).
    getProjectStatus() {
        // Il tag deriva dallo "Stato dell'immobile". Lo stato
        // "Nuovo / In costruzione" arriva dalla sync come flag booleano
        // `isNewConstruction`, gli altri stati come stringa `propertyCondition`.
        //   Nuovo / In costruzione → "Nuova costruzione"
        //   Ottimo / Ristrutturato → "Già ristrutturato"
        //   Buono / Abitabile, Da ristrutturare (e stato non valorizzato) → nessun tag
        const condition = this.propertyCondition.toLowerCase();
        if (this.isNewConstruction || condition.includes('nuovo') || condition.includes('costruzione')) {
            return 'Nuova costruzione';
        }
        if (condition.includes('ottimo') || condition.includes('ristrutturato')) {
            return 'Già ristrutturato';
        }
        return '';
    }

    // The room-count noun ("Trilocale") used by titles and the detail
    // features tile.
    getRoomsLabel() {
        switch (this.roomCount) {
            case 1:
            return 'Monolocale';
            case 2:
            return 'Bilocale';
            case 3:
            return 'Trilocale';
            case 4:
            return 'Quadrilocale';
            default:
            return 'Plurilocale';
        }
    }

    getTitle() {
        // Multi-unit: derive from the units' types (v1 behaviour) — the
        // server title describes the parent, not the tagli.
        if (this.getDisplayType() === 'multiple') {
            const plurals = this.houseTypes().map((t) => housetype[t] || t);
            const label = plurals.length > 0 ? plurals.join(' e ') : 'Tagli';
            return `${label} in ${this.addressInfo.toShortAddress()}`;
        }

        // v2 titles are server-computed; keep the v1 client-side derivation
        // as a fallback for docs synced without one.
        if (this.title !== '') return this.title;

        return `${this.getRoomsLabel()} in ${this.addressInfo.toShortAddress()}`;
    }

    // #526: archived/regressed source ads keep their companion doc (see
    // newarc-cloud-functions#162) flagged unavailable instead of deleting it.
    // Single source of truth for "may the public site show this ad": listing
    // queries filter these flags server-side; direct links and the liked-ads
    // lookup check here.
    isAvailable() {
        return this.isArchived !== true && this.isActive === true;
    }

    // #915: un annuncio non disponibile per il pubblico che si può comunque
    // aprire con il suo link privato (l'hash è confrontato dalla pagina).
    hasPrivatePreview() {
        return this.previewTokenHash !== null && this.isArchived !== true;
    }

    // Pillola "Video": basta uno dei due video (#728).
    hasVideo() {
        return !!(this.video || this.videoVertical);
    }

    // Gap G4: v2 price is already numeric. 0 means "not specified" for the
    // grid's numeric filters/sorts (orgPrice); the formatted string is for
    // display only and stays empty when there is no price.
    getPrice() {
        // Multi-unit: the card/filters use the cheapest unit (v1 behaviour).
        if (this.getDisplayType() === 'multiple') {
            let min = 0;
            for (const child of this.children) {
                const p = Number(child.price) || 0;
                if (p > 0 && (p < min || min === 0)) min = p;
            }
            return min;
        }
        return Number.isFinite(this.price) ? this.price : 0;
    }

    getFormattedPrice() {
        if (this.getDisplayType() === 'multiple') {
            // No 'da ' prefix here: the listing cards (PropertyCard/ListingCard)
            // prepend "Da " themselves for displayType !== 'single', matching
            // the v1 WebsiteAd.getFormattedPrice() contract. Returning it here
            // too would double it ("Da da 165.000€").
            const min = this.getPrice();
            return min > 0 ? parseInt(min).toLocaleString() + '€' : '';
        }
        if (!Number.isFinite(this.price)) return '';
        return parseInt(this.price).toLocaleString() + '€';
    }
}
